import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Avatar from "components/shared/Avatar";
import SimpleTable from "components/shared/tables/SimpleTable";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { renderUserName } from "utils/helpers/user";
import { renderSettingsResourceLabel } from "utils/helpers/settings";
import { renderDuration } from "../../../helpers/eventdisplay/renderDuration";
import { cn } from "lib/utils";
import type { SimpleTableColumn } from "components/shared/tables/SimpleTable";

export interface TransferResult {
    status: boolean;
    message?: string;
    schedule?: any;
    user?: any;
}

export const getTransferResults = (response: unknown, user?: any): TransferResult[] => {
    const entries = Array.isArray(response) ? response : [response];
    return entries
        .filter((entry: any) => typeof entry?.status === "boolean")
        .map((entry: any) => ({ ...entry, user: entry?.user ?? user }));
};

const getSection = (schedule: any) =>
    schedule?.sections?.find((section: any) => section?.job || section?.location) ?? schedule?.sections?.[0];

function ResponseTable({ data }: { data: TransferResult[] }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();

    const columns = useMemo<SimpleTableColumn<TransferResult>[]>(() => [
        {
            header: t("employee"),
            cell: (row) => (
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Avatar user={row?.user} size="sm" />
                    <span>{renderUserName(row?.user)}</span>
                </div>
            ),
        },
        {
            header: t("date"),
            cell: (row) => {
                const date = row?.schedule?.date;
                return date ? moment(date).format(dateFormat) : "-";
            },
        },
        {
            header: t("time"),
            cell: (row) => {
                const startDate = row?.schedule?.startDate;
                const endDate = row?.schedule?.endDate;

                if (!startDate || !endDate) {
                    return <span className="tw-text-neutral-400">-</span>;
                }

                return (
                    <span className="tw-px-3 tw-py-1 tw-rounded-lg tw-text-xs tw-font-semibold tw-bg-neutral-100 tw-text-neutral-700">
                        {moment.parseZone(startDate).format(timeFormat)}
                        {" - "}
                        {moment.parseZone(endDate).format(timeFormat)}
                    </span>
                );
            },
        },
        {
            header: t("duration"),
            cell: (row) => {
                const duration = row?.schedule?.duration;
                return duration ? renderDuration(duration) : "-";
            },
        },
        {
            header: t("job"),
            cell: (row) => renderSettingsResourceLabel(getSection(row?.schedule)?.job) || "-",
        },
        {
            header: t("location"),
            cell: (row) => renderSettingsResourceLabel(getSection(row?.schedule)?.location) || "-",
        },
        {
            header: t("status"),
            cell: (row) => (
                <span
                    className={cn(
                        "tw-px-3 tw-py-1 tw-rounded-lg tw-text-xs tw-font-semibold",
                        row?.status
                            ? "tw-text-green-700 tw-bg-green-100"
                            : "transfer-result-failed tw-text-rose-700 tw-bg-rose-100"
                    )}
                >
                    {t(row?.status ? "success" : "failed")}
                </span>
            ),
        },
        {
            header: t("message"),
            cell: (row) => (
                row?.message ? (
                    <span className={cn("tw-text-xs", !row?.status && "tw-text-rose-600")}>
                        {row.message}
                    </span>
                ) : (
                    <span className="tw-text-neutral-400">-</span>
                )
            ),
        },
    ], [t, dateFormat, timeFormat]);

    return (
        <SimpleTable<TransferResult>
            className={cn(
                "tw-whitespace-nowrap",
                // failed rows are flagged by the marker class on their status badge
                "[&_tr:has(.transfer-result-failed)]:tw-bg-rose-50",
                "[&_tr:has(.transfer-result-failed):hover]:tw-bg-rose-100"
            )}
            data={data}
            columns={columns}
            getRowKey={(row, index) => row?.schedule?.id ?? index}
        />
    );
}

export default ResponseTable;
